import React from "react";

type Props = {
  size?: number;
  className?: string;
  color?: string;
  /** 0.. ~1.5 */
  intensity?: number;
  durationSec?: number;
};

export function AnimatedNoboriLogoScanner({
  size = 48,
  className,
  color = "#FF5E00",
  intensity = 1.15,
  durationSec = 1.15,
}: Props) {
  const gid = React.useId();
  const clipId = `clip-${gid}`;
  const maskSoftId = `mask-soft-${gid}`;
  const maskCoreId = `mask-core-${gid}`;
  const softGradId = `soft-grad-${gid}`;
  const coreGradId = `core-grad-${gid}`;
  const glowFilterId = `glow-${gid}`;

  const d =
    "M115.129 48.5806C115.129 47.5652 114.306 46.7419 113.29 46.7419H41.5806V85.9636H15.8386V21H113.29C128.523 21 140.871 33.3483 140.871 48.5806V117.383H58.691C58.6084 117.389 58.4813 117.398 58.3148 117.414C57.8458 117.459 57.09 117.551 56.1484 117.732C54.2082 118.106 51.7972 118.795 49.5289 119.99C47.3069 121.16 45.4456 122.692 44.1151 124.728C42.8372 126.684 41.5806 129.81 41.5806 135H15.8386C15.8386 125.318 18.2866 117.197 22.5659 110.648C26.7927 104.179 32.342 99.9481 37.5297 97.2149C42.6708 94.5063 47.669 93.1495 51.2859 92.4538C53.1229 92.1005 54.6832 91.9027 55.8351 91.7912C56.4131 91.7353 56.8946 91.7006 57.2635 91.679C57.4481 91.6682 57.6055 91.6609 57.733 91.6557C57.7967 91.653 57.853 91.651 57.9018 91.6494C57.9261 91.6486 57.9486 91.6482 57.9692 91.6476C57.9794 91.6473 57.9895 91.6469 57.9988 91.6467C58.0035 91.6465 58.0108 91.6458 58.0132 91.6458C58.0196 91.6487 58.3427 91.6413 58.3427 91.6413L58.0257 91.6458L58.1873 91.6413H115.129V48.5806Z";

  const fromX = -260;
  const toX = 260;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 156 156"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <defs>
        <clipPath id={clipId}>
          <rect
            width="125.032"
            height="114"
            fill="white"
            transform="translate(15.8386 21)"
          />
        </clipPath>

        <filter id={glowFilterId} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="3.2" result="blur" />
          <feColorMatrix
            in="blur"
            type="matrix"
            values="
              1 0 0 0 0
              0 1 0 0 0
              0 0 1 0 0
              0 0 0 0.85 0"
            result="glow"
          />
          <feMerge>
            <feMergeNode in="glow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        <linearGradient id={softGradId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="rgba(255,255,255,0)" />
          <stop offset="35%" stopColor={`rgba(255,255,255,${0.0})`} />
          <stop
            offset="50%"
            stopColor={`rgba(255,255,255,${0.9 * intensity})`}
          />
          <stop offset="65%" stopColor={`rgba(255,255,255,${0.0})`} />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>

        <linearGradient id={coreGradId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="rgba(255,255,255,0)" />
          <stop offset="46%" stopColor={`rgba(255,255,255,0)`} />
          <stop offset="50%" stopColor={`rgba(255,255,255,${1.0})`} />
          <stop offset="54%" stopColor={`rgba(255,255,255,0)`} />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>

        <mask id={maskSoftId}>
          <rect width="156" height="156" fill="black" />
          <g transform="rotate(-18 78 78)">
            <rect
              x={fromX}
              y="0"
              width="160"
              height="156"
              fill={`url(#${softGradId})`}
            >
              <animate
                attributeName="x"
                from={fromX}
                to={toX}
                dur={`${durationSec}s`}
                repeatCount="indefinite"
              />
            </rect>
          </g>
        </mask>

        <mask id={maskCoreId}>
          <rect width="156" height="156" fill="black" />
          <g transform="rotate(-18 78 78)">
            <rect
              x={fromX}
              y="0"
              width="120"
              height="156"
              fill={`url(#${coreGradId})`}
            >
              <animate
                attributeName="x"
                from={fromX}
                to={toX}
                dur={`${durationSec}s`}
                repeatCount="indefinite"
              />
            </rect>
          </g>
        </mask>
      </defs>

      <g clipPath={`url(#${clipId})`}>
        <path d={d} fill={color} opacity="0.98" />

        <g mask={`url(#${maskSoftId})`} filter={`url(#${glowFilterId})`}>
          <path d={d} fill="white" opacity={0.55 * intensity} />
        </g>

        <g mask={`url(#${maskCoreId})`}>
          <path d={d} fill="white" opacity={0.85} />
        </g>

        <path d={d} fill={color} opacity="0">
          <animate
            attributeName="opacity"
            values="0;0.06;0"
            dur="1.35s"
            repeatCount="indefinite"
          />
        </path>
      </g>
    </svg>
  );
}
